import { useEffect, useRef, useState } from 'react';
import IdentityScreen from './ui/IdentityScreen.jsx';
import StartScreen from './ui/StartScreen.jsx';
import ProgressScreen from './ui/ProgressScreen.jsx';
import ResultsScreen from './ui/ResultsScreen.jsx';
import { runAllProbes, buildSchedule } from './orchestrator.js';
import { supabase, isSupabaseEnabled } from './lib/supabase.js';
import { buildFailedTestResult, recordAttemptEvent, saveTestResult } from './persistence.js';
import { getBrowserPreflight } from './machine.js';
import { resolveServerUrls } from './config.js';

const { serverHttpUrl: SERVER_HTTP_URL, serverWsUrl: SERVER_WS_URL } = resolveServerUrls();
const HEARTBEAT_INTERVAL_MS = 15_000;

function createAttemptId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function elapsedSince(startedAtMs) {
  return startedAtMs ? Math.max(0, Date.now() - startedAtMs) : null;
}

export default function App() {
  const [preflight] = useState(() => getBrowserPreflight());
  const [state, setState] = useState(() => preflight.supported ? 'identity' : 'unsupported');
  const [identity, setIdentity] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Save state: 'idle' | 'saving' | 'saved' | 'failed' | 'disabled'
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const startedAtRef = useRef(null);
  const tickRef = useRef(null);
  const attemptIdRef = useRef(null);
  const phaseRef = useRef(null);
  const lastHeartbeatAtRef = useRef(0);

  const schedule = buildSchedule({});
  const totalSec = Math.ceil(schedule.totalMs / 1000);

  const writeAttemptEvent = ({
    attemptId = attemptIdRef.current,
    eventType,
    phase = phaseRef.current,
    elapsedMs = elapsedSince(startedAtRef.current),
    identity: eventIdentity = identity,
    detail = null
  }) => {
    if (!isSupabaseEnabled() || !attemptId || !eventType) return;
    void recordAttemptEvent(supabase, {
      attemptId,
      eventType,
      phase,
      elapsedMs,
      identity: eventIdentity,
      detail
    }).then(res => {
      if (!res.ok) console.warn('[persistence] attempt event failed:', res.error);
    });
  };

  const persist = async (r) => {
    if (!isSupabaseEnabled()) {
      setSaveStatus('disabled');
      setSaveError(null);
      return;
    }
    setSaveStatus('saving');
    setSaveError(null);
    const res = await saveTestResult(supabase, r);
    if (res.ok) {
      setSaveStatus('saved');
      writeAttemptEvent({
        attemptId: r.attempt_id,
        eventType: 'save_succeeded',
        phase: r.last_phase || phaseRef.current,
        identity: r.identity,
        detail: { overall_result: r.overall_result, attempt_status: r.attempt_status || 'completed' }
      });
    } else {
      setSaveStatus('failed');
      setSaveError(res.error || 'Unknown error');
      writeAttemptEvent({
        attemptId: r.attempt_id,
        eventType: 'save_failed',
        phase: r.last_phase || phaseRef.current,
        identity: r.identity,
        detail: { error: res.error || 'Unknown error', attempt_status: r.attempt_status || null }
      });
      console.warn('[persistence] save failed:', res.error);
    }
  };

  const retrySave = async () => {
    if (result) await persist(result);
  };

  const onIdentitySubmit = (id) => {
    setIdentity(id);
    setState('checklist');
  };

  const onStart = async (checklistData) => {
    const attemptId = createAttemptId();
    attemptIdRef.current = attemptId;
    phaseRef.current = 'machine-check';
    lastHeartbeatAtRef.current = Date.now();
    setChecklist(checklistData);
    setState('running');
    setError(null);
    setResult(null);
    setSaveStatus('idle');
    setSaveError(null);
    setPhase('machine-check');
    setProgress(null);
    setElapsed(0);
    startedAtRef.current = Date.now();
    writeAttemptEvent({
      attemptId,
      eventType: 'started',
      phase: 'machine-check',
      elapsedMs: 0,
      identity,
      detail: {
        confirmed_wired: checklistData.confirmed_wired,
        confirmed_apps_closed: checklistData.confirmed_apps_closed,
        confirmed_work_hours: checklistData.confirmed_work_hours
      }
    });
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    try {
      const r = await runAllProbes({
        serverHttpUrl: SERVER_HTTP_URL,
        serverWsUrl: SERVER_WS_URL,
        schedule,
	        onPhaseChange: ({ phase: p }) => {
	          if (!p) return;
          phaseRef.current = p;
	          setPhase(p);
	          setProgress(null);
          writeAttemptEvent({
            attemptId,
            eventType: 'phase',
            phase: p,
            identity,
            detail: { phase: p }
          });
	        },
	        onProgress: p => {
          setProgress(p);
          const now = Date.now();
          if (now - lastHeartbeatAtRef.current >= HEARTBEAT_INTERVAL_MS) {
            lastHeartbeatAtRef.current = now;
            writeAttemptEvent({
              attemptId,
              eventType: 'heartbeat',
              phase: phaseRef.current,
              identity,
              detail: p
            });
          }
        }
	      });
      phaseRef.current = 'completed';
      const augmented = {
        ...r,
        attempt_id: attemptId,
        attempt_status: 'completed',
        last_phase: 'completed',
        identity,
        checklist: checklistData
      };
      setResult(augmented);
      setState('done');
      writeAttemptEvent({
        attemptId,
        eventType: 'completed',
        phase: 'completed',
        identity,
        detail: { overall_result: augmented.overall_result }
      });
      // Fire-and-forget: results screen renders immediately; save runs in parallel.
      persist(augmented);
    } catch (err) {
      const failurePhase = phaseRef.current || phase;
      const failedResult = buildFailedTestResult({
        attemptId,
        identity,
        checklist: checklistData,
        phase: failurePhase,
        error: err,
        startedAt: startedAtRef.current ? new Date(startedAtRef.current).toISOString() : new Date().toISOString(),
        completedAt: new Date().toISOString()
      });
      setResult(failedResult);
      writeAttemptEvent({
        attemptId,
        eventType: 'failed',
        phase: failurePhase,
        identity,
        detail: { error: String(err), phase: failurePhase }
      });
      void persist(failedResult);
      setError(String(err));
      setState('error');
    } finally {
      if (tickRef.current) clearInterval(tickRef.current);
    }
  };

  useEffect(() => {
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      {state === 'unsupported' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-900">
          <h1 className="text-xl font-semibold">Browser not supported</h1>
          <p className="mt-2">
            {preflight.reason || 'This browser cannot run the connection test. Use current Chrome, Edge, Firefox, or Safari on a desktop or laptop, then reload.'}
          </p>
        </div>
      )}
      {state === 'identity' && <IdentityScreen onContinue={onIdentitySubmit} />}
      {state === 'checklist' && (
        <StartScreen identity={identity} onStart={onStart} onBack={() => setState('identity')} />
      )}
      {state === 'running' && (
        <ProgressScreen phase={phase} elapsedSec={elapsed} totalSec={totalSec} progress={progress} />
      )}
      {state === 'done' && result && (
        <ResultsScreen
          result={result}
          saveStatus={saveStatus}
          saveError={saveError}
          onRetrySave={retrySave}
        />
      )}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          <p className="font-semibold">The test could not finish.</p>
          <p className="mt-1 break-words">{error}</p>
        </div>
      )}
    </main>
  );
}
